/**
 * 引用行を除外してテキストを取得（&gt; または > で始まる行を除外）
 * @param text - プレーンテキスト
 * @returns 引用行を除いたテキスト
 */
export function stripQuoteLines(text: string): string {
  return text
    .split("\n")
    .filter((line) => {
      const trimmed = line.trim();
      return !trimmed.startsWith("&gt;") && !trimmed.startsWith(">");
    })
    .join("\n");
}

/**
 * 引用ブロックの情報。
 * textは引用記号を除いた検索用の本文、sourceは表示中の引用記法を保持する。
 */
export interface QuoteTextBlock {
  text: string;
  source: string;
  lineIndexes: number[];
}

export interface QuoteSearchCandidate {
  id: string;
  seq: number;
  postSequenceNo: number | null;
  searchText: string;
}

interface QuoteSearchCoverage {
  startIndex: number;
  endIndex: number;
  matchIndex: number | null;
}

interface PartialQuoteSearchCacheEntry {
  kind: "partial";
  coverage: QuoteSearchCoverage[];
}

interface FullQuoteSearchCacheEntry {
  kind: "full";
  candidates: readonly QuoteSearchCandidate[];
}

type QuoteSearchCacheEntry =
  | PartialQuoteSearchCacheEntry
  | FullQuoteSearchCacheEntry;

export interface QuoteSearchIndex {
  byPostSequenceNo: ReadonlyMap<number, QuoteSearchCandidate>;
  bySeqFallback: ReadonlyMap<number, QuoteSearchCandidate>;
  descending: readonly QuoteSearchCandidate[];
  matchingCandidatesByText: Map<string, readonly QuoteSearchCandidate[]>;
}

const MAX_CACHED_QUOTE_SEARCHES = 128;
const quoteSearchCacheStateByIndex = new WeakMap<
  QuoteSearchIndex,
  QuoteSearchCacheState
>();

interface QuoteSearchCacheState {
  partialSearchesByText: Map<string, PartialQuoteSearchCacheEntry>;
  entryKindsByText: Map<string, QuoteSearchCacheEntry["kind"]>;
}

export function createQuoteSearchIndex(
  candidates: readonly QuoteSearchCandidate[],
): QuoteSearchIndex {
  const descending = [...candidates].sort(
    (left, right) => right.seq - left.seq,
  );
  return {
    byPostSequenceNo: new Map(
      candidates.flatMap((candidate) =>
        candidate.postSequenceNo === null
          ? []
          : [[candidate.postSequenceNo, candidate] as const],
      ),
    ),
    bySeqFallback: new Map(
      candidates.flatMap((candidate) =>
        candidate.postSequenceNo === null || candidate.seq === 0
          ? [[candidate.seq, candidate] as const]
          : [],
      ),
    ),
    descending,
    matchingCandidatesByText: new Map(),
  };
}

/** Resolve a displayed quote against prior posts without depending on Post. */
export function resolveQuoteSearchCandidate(
  quoteLine: string,
  currentSeq: number,
  index: QuoteSearchIndex,
): QuoteSearchCandidate | null {
  const quoteTexts = quoteLine.split(/\r?\n/).map(parseQuoteText);
  if (quoteTexts.some((quoteText) => quoteText === null)) return null;
  const firstQuoteText = quoteTexts[0];
  if (quoteTexts.length === 1) {
    return firstQuoteText === null
      ? null
      : findFirstQuoteCandidate(firstQuoteText, currentSeq, index);
  }

  const quoteTextCounts = new Map<
    string,
    { quoteText: string; count: number }
  >();
  for (const quoteText of quoteTexts) {
    if (quoteText === null) continue;
    const key = quoteText.toLowerCase();
    const existing = quoteTextCounts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      quoteTextCounts.set(key, { quoteText, count: 1 });
    }
  }

  const matchCounts = new Map<string, number>();
  for (const { quoteText, count } of quoteTextCounts.values()) {
    for (const candidate of findQuoteCandidates(quoteText, currentSeq, index)) {
      matchCounts.set(
        candidate.id,
        (matchCounts.get(candidate.id) ?? 0) + count,
      );
    }
  }

  let bestCandidate: QuoteSearchCandidate | null = null;
  let bestMatchCount = 0;
  for (const candidate of index.descending) {
    const matchCount = matchCounts.get(candidate.id) ?? 0;
    if (matchCount > bestMatchCount) {
      bestCandidate = candidate;
      bestMatchCount = matchCount;
    }
  }
  return bestCandidate;
}

function findFirstQuoteCandidate(
  quoteText: string,
  currentSeq: number,
  index: QuoteSearchIndex,
): QuoteSearchCandidate | null {
  const directNo = quoteText.match(/^No\.\s*(\d+)$/i)?.[1];
  if (directNo !== undefined) {
    const no = Number(directNo);
    const candidate =
      index.byPostSequenceNo.get(no) ?? index.bySeqFallback.get(no);
    return candidate && candidate.seq < currentSeq ? candidate : null;
  }

  const key = quoteText.toLowerCase();
  const cachedCandidates = index.matchingCandidatesByText.get(key);
  if (cachedCandidates !== undefined) {
    return (
      cachedCandidates[firstCandidateBeforeSeq(cachedCandidates, currentSeq)] ??
      null
    );
  }

  const firstPriorCandidate = firstCandidateBeforeSeq(
    index.descending,
    currentSeq,
  );
  if (firstPriorCandidate >= index.descending.length) return null;

  const cacheState = getQuoteSearchCacheState(index);
  const partialSearch = cacheState.partialSearchesByText.get(key) ?? {
    kind: "partial" as const,
    coverage: [],
  };
  const { containingSegment, insertionIndex } = findCoveragePosition(
    partialSearch.coverage,
    firstPriorCandidate,
  );
  if (containingSegment) {
    return containingSegment.matchIndex === null
      ? null
      : (index.descending[containingSegment.matchIndex] ?? null);
  }

  const nextSegment = partialSearch.coverage[insertionIndex];
  const endIndex = nextSegment?.startIndex ?? index.descending.length;
  for (let position = firstPriorCandidate; position < endIndex; position += 1) {
    const candidate = index.descending[position];
    if (
      candidate &&
      candidate.seq < currentSeq &&
      candidate.searchText.includes(key)
    ) {
      partialSearch.coverage.splice(insertionIndex, 0, {
        startIndex: firstPriorCandidate,
        endIndex: position + 1,
        matchIndex: position,
      });
      cacheQuoteSearch(index, key, partialSearch);
      return candidate;
    }
  }

  if (nextSegment) {
    partialSearch.coverage.splice(insertionIndex, 1, {
      startIndex: firstPriorCandidate,
      endIndex: nextSegment.endIndex,
      matchIndex: nextSegment.matchIndex,
    });
    cacheQuoteSearch(index, key, partialSearch);
    return nextSegment.matchIndex === null
      ? null
      : (index.descending[nextSegment.matchIndex] ?? null);
  }

  partialSearch.coverage.splice(insertionIndex, 0, {
    startIndex: firstPriorCandidate,
    endIndex: index.descending.length,
    matchIndex: null,
  });
  cacheQuoteSearch(index, key, partialSearch);
  return null;
}

function findQuoteCandidates(
  quoteText: string,
  currentSeq: number,
  index: QuoteSearchIndex,
): QuoteSearchCandidate[] {
  const directNo = quoteText.match(/^No\.\s*(\d+)$/i)?.[1];
  if (directNo !== undefined) {
    const no = Number(directNo);
    const candidate =
      index.byPostSequenceNo.get(no) ?? index.bySeqFallback.get(no);
    return candidate && candidate.seq < currentSeq ? [candidate] : [];
  }

  const candidates = getMatchingQuoteCandidates(quoteText, index);
  return candidates.slice(firstCandidateBeforeSeq(candidates, currentSeq));
}

function getMatchingQuoteCandidates(
  quoteText: string,
  index: QuoteSearchIndex,
): readonly QuoteSearchCandidate[] {
  const key = quoteText.toLowerCase();
  const cached = index.matchingCandidatesByText.get(key);
  if (cached !== undefined) return cached;

  const matches = index.descending.filter((candidate) =>
    candidate.searchText.includes(key),
  );
  cacheQuoteSearch(index, key, { kind: "full", candidates: matches });
  return matches;
}

function cacheQuoteSearch(
  index: QuoteSearchIndex,
  key: string,
  entry: QuoteSearchCacheEntry,
): void {
  const cacheState = getQuoteSearchCacheState(index);
  const existingKind =
    cacheState.entryKindsByText.get(key) ??
    (index.matchingCandidatesByText.has(key) ? "full" : undefined);
  if (
    existingKind === undefined &&
    cacheState.entryKindsByText.size >= MAX_CACHED_QUOTE_SEARCHES
  ) {
    const oldest = cacheState.entryKindsByText.keys().next().value;
    if (oldest !== undefined) {
      const oldestKind = cacheState.entryKindsByText.get(oldest);
      if (oldestKind === "full") index.matchingCandidatesByText.delete(oldest);
      else cacheState.partialSearchesByText.delete(oldest);
      cacheState.entryKindsByText.delete(oldest);
    }
  }

  if (entry.kind === "full") {
    cacheState.partialSearchesByText.delete(key);
    index.matchingCandidatesByText.set(key, entry.candidates);
  } else {
    index.matchingCandidatesByText.delete(key);
    cacheState.partialSearchesByText.set(key, entry);
  }
  cacheState.entryKindsByText.set(key, entry.kind);
}

function getQuoteSearchCacheState(
  index: QuoteSearchIndex,
): QuoteSearchCacheState {
  const cached = quoteSearchCacheStateByIndex.get(index);
  if (cached) return cached;

  const cacheState: QuoteSearchCacheState = {
    partialSearchesByText: new Map(),
    entryKindsByText: new Map(
      [...index.matchingCandidatesByText.keys()].map(
        (key) => [key, "full"] as const,
      ),
    ),
  };
  quoteSearchCacheStateByIndex.set(index, cacheState);
  return cacheState;
}

function findCoveragePosition(
  coverage: readonly QuoteSearchCoverage[],
  index: number,
): { containingSegment: QuoteSearchCoverage | null; insertionIndex: number } {
  let low = 0;
  let high = coverage.length;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    const segment = coverage[middle];
    if (segment && segment.startIndex <= index) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  const previousSegment = coverage[low - 1];
  if (previousSegment && index < previousSegment.endIndex) {
    return { containingSegment: previousSegment, insertionIndex: low };
  }
  return { containingSegment: null, insertionIndex: low };
}

/** Find the first descending candidate whose sequence is below currentSeq. */
function firstCandidateBeforeSeq(
  candidates: readonly QuoteSearchCandidate[],
  currentSeq: number,
): number {
  let low = 0;
  let high = candidates.length;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    const candidate = candidates[middle];
    if (candidate && candidate.seq >= currentSeq) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  return low;
}

function parseQuoteLine(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith("&gt;") && !trimmed.startsWith(">")) return null;
  const quoteText = trimmed.replace(/^(?:(?:&gt;|>)+)\s*/, "").trim();
  return quoteText || null;
}

/**
 * 引用記法を解析する。複数行の場合は各行の引用記号を除去して結合する。
 * 引用でない行や内容が空の引用を含む場合はnullを返す。
 */
export function parseQuoteText(text: string): string | null {
  const quoteTexts = text.split(/\r?\n/).map(parseQuoteLine);
  if (quoteTexts.some((quoteText) => quoteText === null)) return null;
  return quoteTexts.join("\n") || null;
}

/**
 * 引用検索へ渡す本文を引用記号なしに正規化する。
 */
export function getQuoteSearchText(quoteText: string): string {
  return parseQuoteText(quoteText) ?? quoteText.replace(/^>/g, "").trim();
}

/**
 * 本文から連続する引用行をブロック単位で抽出する。
 */
export function extractQuoteTextBlocks(text: string): QuoteTextBlock[] {
  const blocks: QuoteTextBlock[] = [];
  let currentText: string[] = [];
  let currentSource: string[] = [];
  let currentLineIndexes: number[] = [];

  const flush = (): void => {
    if (currentText.length === 0) return;
    blocks.push({
      text: currentText.join("\n"),
      source: currentSource.join("\n"),
      lineIndexes: currentLineIndexes,
    });
    currentText = [];
    currentSource = [];
    currentLineIndexes = [];
  };

  text.split(/\r?\n/).forEach((line, lineIndex) => {
    const quoteText = parseQuoteLine(line);
    if (quoteText === null) {
      flush();
      return;
    }
    // 空白の許容範囲は resolveQuotedPost 側と揃える。片側だけ緩めると
    // 「> No. 123」が独立ブロックにならず、レス番号への解決先を失う。
    if (/^No\.\s*\d+$/i.test(quoteText)) {
      flush();
      blocks.push({
        text: quoteText,
        source: line,
        lineIndexes: [lineIndex],
      });
      return;
    }
    currentText.push(quoteText);
    currentSource.push(line);
    currentLineIndexes.push(lineIndex);
  });
  flush();

  return blocks;
}

/**
 * 本文から引用本文をブロック単位で抽出する。
 */
export function extractQuoteTexts(text: string): string[] {
  return extractQuoteTextBlocks(text).map((block) => block.text);
}
